<template>
    <stagger-fade-horizontal-animation
        :reverse="reversed"
        :trigger="() => $refs.section"
        :elements="[
            {
                element: () => $refs.imageColumn,
            },
            {
                element: () => $refs.textColumn,
            },
        ]"
    >
        <component
            :is="backgroundImage.src ? 'lazy-background' : 'section'"
            class="horizontal-text-image-section"
            :src="backgroundImage.src"
            :alt="backgroundImage.alt"
            :class="[
                `columns-${ textColumnWidth }`,
                `background-${ backgroundVariant }`,
                {
                    reversed,
                },
            ]"
            v-test:sectionBackground
            ref="section"
        >
            <b-container
                :fluid="fluidContainer"
                v-test:container
            >
                <b-row class="content-row">
                    <b-col
                        class="image-column"
                        :class="uid"
                        :md="12 - textColumns"
                        v-test:imageColumn
                        ref="imageColumn"
                    >
                        <b-row class="mobile-image-row">
                            <div class="image-container">
                                <lazy-image
                                    class="mobile-image"
                                    :src="mobileImage.src"
                                    :alt="mobileImage.alt"
                                    :width="mobileImage.width"
                                    :height="mobileImage.height"
                                    v-test:mobileImage
                                />

                                <lazy-image
                                    class="desktop-image"
                                    :src="desktopImage.src"
                                    :alt="desktopImage.alt"
                                    :width="desktopImage.width"
                                    :height="desktopImage.height"
                                    v-test:desktopImage
                                />
                            </div>
                        </b-row>
                    </b-col>
                    <b-col
                        class="text-column"
                        :class="uid"
                        :md="textColumns"
                        v-test:textColumn
                        ref="textColumn"
                    >
                        <mobile-gutter>
                            <div class="text">
                                <h2 v-test:title>{{ title }}</h2>

                                <markdown-content
                                    :content="text"
                                />
                            </div>
                        </mobile-gutter>
                    </b-col>
                </b-row>
            </b-container>
        </component>
    </stagger-fade-horizontal-animation>
</template>

<script>
    import MarkdownContent from '../../utils/MarkdownContent/MarkdownContent';
    import { ITSU_VARIANT_DEFAULT, ITSU_VARIANTS } from '../../../plugins/constants';

    const columnWidths = {
        half: 6,
        one_third: 4,
        two_thirds: 8,
        one_quarter: 3,
        three_quarters: 9,
    };

    export default {
        name: 'horizontal-text-image-section',

        components: {
            MarkdownContent,
        },

        props: {
            title: {
                type: String,
                required: true,
            },

            text: {
                type: String,
                required: true,
            },

            desktopImage: {
                type: Object,
                default: () => ({}),
            },

            mobileImage: {
                type: Object,
                default: () => ({}),
            },

            backgroundImage: {
                type: Object,
                default: () => ({}),
            },

            reversed: {
                type: Boolean,
                default: false,
            },

            fluidContainer: {
                type: Boolean,
                default: false,
            },

            textColumnWidth: {
                type: String,
                default: 'two_thirds',
                validator: (val) => Object.keys(columnWidths).includes(val),
            },

            backgroundVariant: {
                type: String,
                default: ITSU_VARIANT_DEFAULT,
                validator: (val) => ITSU_VARIANTS.includes(val),
            },
        },

        computed: {
            /**
             * Return the column count for the provided text column width.
             *
             * @returns {number}
             */
            textColumns() {
                return columnWidths[this.textColumnWidth];
            },
        },
    };
</script>

<style lang="scss" scoped>
    $desktop-breakpoint: md;

    .horizontal-text-image-section {
        font-size: rem(20px);
        overflow: hidden;

        @include media-breakpoint-up($desktop-breakpoint) {
            font-size: rem(24px);
        }
    }

    .container {
        @include media-breakpoint-only(sm) {
            max-width: none;
        }
    }

    @each $variant, $colour in $itsu-variants {
        .background-#{ $variant } {
            background-color: $colour;
        }
    }

    .background-blue,
    .background-pink {
        color: #ffffff;

        h2 {
            color: inherit;
        }
    }

    .reversed {
        .content-row {
            @include media-breakpoint-up($desktop-breakpoint) {
                flex-direction: row-reverse;
            }
        }

        .image-container {
            justify-content: flex-start;

            @include media-breakpoint-up($desktop-breakpoint) {
                padding-left: 50px;
                padding-right: 0;
            }
        }
    }

    .content-row {
        position: relative;
    }

    .image-container {
        display: flex;
        height: 100%;

        @include media-breakpoint-up($desktop-breakpoint) {
            justify-content: flex-end;
            padding-right: 50px;
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
        }
    }

    .lazy-image.mobile-image {
        width: 100%;
        height: auto;

        @include media-breakpoint-up($desktop-breakpoint) {
            display: none;
        }
    }

    .lazy-image.desktop-image {
        display: none;

        @include media-breakpoint-up($desktop-breakpoint) {
            display: inline;
        }
    }

    .text-column {
        @include media-breakpoint-up($desktop-breakpoint) {
            display: flex;
            align-items: center;
        }
    }

    .text {
        text-align: center;
        padding: 15px 0;

        @include media-breakpoint-up($desktop-breakpoint) {
            padding: 114px 0;
        }
    }

    h2 {
        font-weight: bold;
        color: primary-palette(1);
        margin-bottom: 0.6em;
    }

    .text ::v-deep {
        p:not(:last-child) {
            margin-bottom: 1.5em;

            @include media-breakpoint-up($desktop-breakpoint) {
                margin-bottom: 2.5em;
            }
        }

        p:last-child {
            margin-bottom: 0;
        }
    }

    .mobile-image-row {
        display: block;
    }

    .image-column .mobile-image-row,
    .image-column .desktop-image {
        height: 100%;
        width: auto;
    }
</style>
